import { apiClient } from "@/services/api"
import { i18n } from "@/plugins/i18n"
import { comparisonUnit, convertUnitPrice, rowUnitOf, unitFamily } from "@/services/machinePurchaseUnits"
import { FIRST_YEAR, LAST_YEAR } from "@/services/supplierLimits"

// The supplier price rows of machines and materials and their dated prices (docs/suppliers.md, "Prices"). Plain
// requests rather than a store: each view loads what it shows and reloads after a change, nothing is shared.
// All of it answers 409 SUPPLIER_SETUP_REQUIRED until the workspace's setup is done.

const headers = { tenant: true, authorization: true }

/** `MACHINE` / `MATERIAL` → the path segment of its records. */
export const ITEM_PATHS = { MACHINE: "machine", MATERIAL: "material" }

const rowsPath = (kind, itemId) => `/${ITEM_PATHS[kind]}/${itemId}`

// The server dates "gilt ab" in the workspace's zone, Europe/Berlin; the browser's own calendar is a day off
// for somebody abroad or around midnight. `en-CA` formats as YYYY-MM-DD.
const SERVER_DAY = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Europe/Berlin",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
})

/** Today as the server counts days, as the `YYYY-MM-DD` the routes take. */
export const todayIso = () => SERVER_DAY.format(new Date())

/**
 * A `LewDateInput` rule for a day the server stores (a price's "gilt ab", a discount's start): it takes the years
 * `FIRST_YEAR`..`LAST_YEAR` only and refuses anything else for the whole save. The rule sees the field's text
 * (dd.mm.yyyy); text that is no date yet is the field's own rule's.
 */
export const withinServerDays = text => {
  const year = Number(/^\d{2}\.\d{2}\.(\d{4})$/.exec(text ?? "")?.[1])
  return !year || (year >= FIRST_YEAR && year <= LAST_YEAR) || i18n.global.t("supplier.prices.valid_from_window")
}

// One row per supplier and qualifier on an item: the server tells two apart by the qualifier trimmed, in any case.
const sameQualifier = qualifier => (qualifier ?? "").trim().toLowerCase()

/**
 * Under a new row of a supplier who has the item already, which qualifiers its rows have: "Schon im Sortiment: ohne
 * Zusatz, „Big Bag“. Für einen zweiten Preis …". `taken` are those rows' qualifiers, each in the language's quotes;
 * as typed, not escaped (it is shown as text).
 */
export function takenQualifiersNote(taken) {
  const t = i18n.global.t
  const named = taken.map(qualifier =>
    sameQualifier(qualifier)
      ? t("supplier.prices.taken.quoted", { qualifier: qualifier.trim() }, { escapeParameter: false })
      : t("supplier.prices.taken.without_qualifier")
  )
  return `${t("supplier.prices.taken.in_assortment")}: ${named.join(", ")}. ${t("supplier.prices.taken.second_price")}`
}

/** The qualifier rule of a new row beside the supplier's `taken` ones: none of theirs again, "none" included. */
export const distinctQualifierRule = taken => value => {
  const t = i18n.global.t
  const others = taken.map(sameQualifier)
  if (!others.length) return true
  const typed = sameQualifier(value)
  if (!typed) return !others.includes("") || t("supplier.prices.taken.qualifier_needed")
  return !others.includes(typed) || t("supplier.prices.taken.qualifier_taken")
}

export const loadPurchaseOverview = (kind, itemId, { silent = false } = {}) =>
  apiClient.get(`${rowsPath(kind, itemId)}/purchase-overview`, { headers, silent }).then(res => res.data)

export const addSupplierRow = (kind, itemId, row) =>
  apiClient.post(`${rowsPath(kind, itemId)}/supplier-rows`, row, { headers }).then(res => res.data)

export const updateSupplierRow = (kind, itemId, variantId, fields) =>
  apiClient.patch(`${rowsPath(kind, itemId)}/supplier-rows/${variantId}`, fields, { headers })

export const removeSupplierRow = (kind, itemId, variantId) =>
  apiClient.delete(`${rowsPath(kind, itemId)}/supplier-rows/${variantId}`, { headers })

export const setDefaultSupplierRow = (kind, itemId, variantId) =>
  apiClient.put(`${rowsPath(kind, itemId)}/default-supplier-row`, { variantId }, { headers })

export const loadRowRevisions = (kind, itemId, variantId) =>
  apiClient.get(`${rowsPath(kind, itemId)}/supplier-rows/${variantId}/revisions`, { headers }).then(res => res.data)

// `tiers` left out keeps the revision's; `[]` removes them (materials only).
export const updateRevision = (id, { validFrom, prices, tiers }) =>
  apiClient.put(`/purchase-price-revisions/${id}`, { validFrom, prices, tiers }, { headers })

export const deleteRevision = id => apiClient.delete(`/purchase-price-revisions/${id}`, { headers })

export const loadAssortment = (supplierId, { itemKind, search, page = 0, itemsPerPage = 100, silent = false }) =>
  apiClient
    .get(`/supplier/${supplierId}/assortment`, {
      params: { itemKind, search: search || undefined, page, itemsPerPage },
      headers,
      silent,
    })
    .then(res => res.data)

// Several items into a supplier's Sortiment, each a new row with its first price (`{ itemKind, itemId, qualifier,
// articleNumber, prices }`): all or none. A refused row is named in the violation's value (`index`).
export const addSupplierRows = (supplierId, { validFrom, rows }) =>
  apiClient.post(`/supplier/${supplierId}/rows`, { validFrom, rows }, { headers }).then(res => res.data)

export const postSupplierPrices = (supplierId, { validFrom, rows }) =>
  apiClient.post(`/supplier/${supplierId}/prices`, { validFrom, rows }, { headers }).then(res => res.data)

/** `{ state, unassignedRows }`: cheap, for the invitation; the full list is `loadSupplierSetup`. */
export const loadSupplierSetupStatus = () =>
  apiClient.get("/supplier-setup/status", { headers, silent: true }).then(res => res.data)

export const loadSupplierSetup = ({ silent = false } = {}) =>
  apiClient.get("/supplier-setup", { headers, silent }).then(res => res.data)

export const previewSupplierSetup = mappings =>
  apiClient.post("/supplier-setup/preview", { mappings }, { headers }).then(res => res.data)

export const applySupplierSetup = mappings =>
  apiClient.post("/supplier-setup/apply", { mappings }, { headers }).then(res => res.data)

/**
 * "In der Regel": the default row's price and the range over every row, per currency. The currencies the default
 * has a price in come first (that is what the item usually costs), then the workspace's, then the rest. Rows
 * without a price in a currency don't count there; a currency only some rows have still gets its line.
 *
 * A machine's rows may bill in different units (`machineUnit` given: a row without a unit bills in it). They count in
 * the default's unit, or per day where Tag, Woche and Monat mix (`comparisonUnit`); a row that does not convert
 * (Stunde against a day) is left out.
 *
 * @returns `[{ currency, typical, min, max, count, suppliers, unit, mixed }]` — `typical` is the default row's price,
 *   or null when the default has none in that currency; `count` the prices, `suppliers` how many suppliers ask them
 *   (two rows of one supplier are one); `unit` the one the figures are in (machines only), `mixed` whether rows were
 *   converted into it.
 */
export function typicalPrices(rows, workspaceCurrency, { machineUnit = null } = {}) {
  const defaultRow = rows.find(row => row.isDefault)
  const unitOf = row => (machineUnit ? rowUnitOf(row, machineUnit) : null)
  const unit = machineUnit ? comparisonUnit(rows.map(unitOf), unitOf(defaultRow ?? rows[0])) : null
  const inUnit = (row, amount) =>
    amount == null ? null : unit ? convertUnitPrice(amount, unitOf(row), row.daysPerUnit, unit) : Number(amount)
  const counted = rows.filter(row => !unit || unitFamily(unitOf(row)) === unitFamily(unit))
  const rank = currency => (defaultRow?.prices?.[currency] != null ? 0 : currency === workspaceCurrency ? 1 : 2)
  const currencies = [...new Set(counted.flatMap(row => Object.keys(row.prices ?? {})))]
  currencies.sort((a, b) => rank(a) - rank(b) || a.localeCompare(b))
  return currencies.map(currency => {
    const priced = counted.filter(row => inUnit(row, row.prices?.[currency]) != null)
    const amounts = priced.map(row => inUnit(row, row.prices[currency]))
    return {
      currency,
      typical: defaultRow?.prices?.[currency] != null ? inUnit(defaultRow, defaultRow.prices[currency]) : null,
      min: Math.min(...amounts),
      max: Math.max(...amounts),
      count: amounts.length,
      suppliers: supplierCount(priced),
      unit,
      mixed: Boolean(unit) && counted.some(row => unitOf(row) !== unit),
    }
  })
}

/** How many suppliers `rows` (overview rows or a record's variants) name: two rows of one supplier count once. */
export const supplierCount = rows =>
  new Set(rows.map(row => row.supplier?.id ?? row.supplierId ?? row.supplier?.name ?? row.supplierName)).size

/** How far a row's price lies from the default's, in percent, rounded; null where either is missing. */
export function deviationPercent(price, defaultPrice) {
  if (price == null || defaultPrice == null || Number(defaultPrice) === 0) return null
  return Math.round(((Number(price) - Number(defaultPrice)) / Number(defaultPrice)) * 100)
}

/** "Mietpark Nord · Nacht": the label a row is known by, as the server derives it. */
export const rowLabel = row => [row.supplier?.name ?? row.supplierName, row.qualifier].filter(Boolean).join(" · ")

/**
 * The variants a create sends: the draft rows of a create page carry the supplier's name and place for the
 * table, which the server has no field for.
 */
export const requestVariants = variants =>
  // eslint-disable-next-line no-unused-vars
  variants?.map(({ supplierName, supplierPlace, ...variant }) => variant) ?? variants

/**
 * Stale bookings of rows that follow the supplier's price (docs/suppliers.md, "Umstellen"): a backdated price, a
 * corrected one or a backdated discount leaves them at the old price until somebody asks. `scope` is `{ supplierId }`
 * or `{ itemKind, itemId, variantId? }`. Counts only projects the caller may re-price; `unreachableProjects` the
 * others.
 *
 * @returns `{ bookings, projects, since, oldPrices: [{ currency, price, count }], unreachableProjects }`
 */
export const loadFollowingBookings = scope =>
  apiClient.post("/purchase-prices/following-bookings", scope, { headers, silent: true }).then(res => res.data)

/** Puts those bookings on the price of their day. `{ bookings, projects, skipped, unreachableProjects }`. */
export const repriceFollowingBookings = scope =>
  apiClient.post("/purchase-prices/following-bookings/reprice", scope, { headers }).then(res => res.data)
